import React, { useState } from "react";
import { toast } from "sonner";

import { usePatchAgent } from "@/shared/api/agents";
import { clientAppLink } from "@/shared/lib/client-app";
import { LinkField } from "@/shared/components/LinkField";
import { ListCard } from "@/shared/components/ListCard";
import { BrandButton, GhostButton } from "@/shared/components/ui-kit";
import { TokenList } from "./policies/TokenList";

import type { AgentPatch, AgentRow, Visibility as VisibilityValue } from "@/shared/types";

const CHOICES: { value: VisibilityValue; label: string; says: string }[] = [
  { value: "private", label: "Private", says: "Your workspace, and the people you invite." },
  { value: "unlisted", label: "Unlisted", says: "Anyone signed in who has the link, and anyone on its channels." },
  { value: "public", label: "Public", says: "Like unlisted, and listed in the marketplace once we review it." },
];

const same = (a: string[], b: string[]) => a.join("\n") === b.join("\n");

/* Who can find the agent, on every interface: private (and the people it invites), unlisted
   or public. A choice applies at once; invites wait for Save invites, so a half-typed list
   never goes out. A shared agent's link sits where the invites would. */
export function Visibility({ agent }: { agent: AgentRow }) {
  const patch = usePatchAgent();
  /** The invites being edited; null while they are the saved ones. */
  const [invites, setInvites] = useState<string[] | null>(null);
  const editing = invites !== null && !same(invites, agent.invited);

  const save = (body: AgentPatch, said: string) =>
    patch.mutate({ id: agent.id, body }, {
      onSuccess: () => {
        setInvites(null);
        toast.success(said);
      },
    });

  return (
    <ListCard label="Visibility">
      <div role="radiogroup" aria-label="Visibility" className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-px bg-[var(--hairline)]">
        {CHOICES.map(({ value, label, says }) => {
          const on = agent.visibility === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={patch.isPending}
              onClick={() => !on && save({ visibility: value }, `${agent.name} is ${label.toLowerCase()}`)}
              className={`press flex items-start gap-2.5 px-3.5 py-3 text-left disabled:opacity-60 ${
                on ? "bg-[var(--surface-sunk)]" : "bg-[var(--surface)] hover:bg-[var(--surface-sunk)]"
              }`}
            >
              <span
                aria-hidden="true"
                className={`w-4 h-4 mt-px shrink-0 rounded-full bg-[var(--surface)] ${
                  on ? "border-[4.5px] border-[var(--text-primary)]" : "border-[1.5px] border-[var(--hairline-strong)]"
                }`}
              />
              <span className="min-w-0">
                <span className="block text-[13px] font-medium text-[var(--text-primary)]">{label}</span>
                <span className="block mt-0.5 text-[12px] leading-[1.45] text-[var(--text-tertiary)]">{says}</span>
              </span>
            </button>
          );
        })}
      </div>
      <div className="px-3.5 py-3 flex flex-col gap-2">
        {agent.visibility === "private" ? (
          <>
            <span className="text-[12.5px] font-medium text-[var(--text-primary)]">Invited people</span>
            <TokenList label="Invited people" items={invites ?? agent.invited} onChange={setInvites} />
            <p className="m-0 text-[12px] leading-[1.55] text-[var(--text-tertiary)]">
              An email lets one account in; @acme.com lets in everyone at acme.com. Your workspace is always in.
            </p>
            {editing && (
              <div className="flex justify-end gap-1.5">
                <GhostButton onClick={() => setInvites(null)} disabled={patch.isPending}>Discard</GhostButton>
                <BrandButton
                  className="disabled:opacity-40 disabled:pointer-events-none"
                  disabled={patch.isPending}
                  onClick={() => save({ invited: invites }, "Invites saved")}
                >
                  {patch.isPending ? "Saving…" : "Save invites"}
                </BrandButton>
              </div>
            )}
          </>
        ) : (
          <LinkField url={clientAppLink(agent.id)} />
        )}
      </div>
    </ListCard>
  );
}
